import { useTranslate } from "@tolgee/react";
import { useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { isJwtExpired } from "@/helpers/jwtTools";
import { getNavigatorLanguage } from "@/helpers/lang/getNavigatorLanguage";
import { performRefreshToken } from "@/helpers/refreshTokenManager";
import { useToasts } from "@/hooks/useToasts";
import {
  type FieldValues,
  type InitialAIValuesType,
} from "@/pages/OpenBriefPage/BriefSummary/BriefSummary.types";
import { accessTokenAtom } from "@/store/atoms/authTokens";
import {
  type AIChatMessage,
  type ConversationReadyResponse,
} from "./useAIChat.types";

const RESPONSE_TIMEOUT_MS = 120_000;

export const useAIChat = () => {
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [conversationId, setConversationId] = useState("");

  const [initialAIValues, setInitialAIValues] = useState<InitialAIValuesType>();
  const [latestFields, setLatestFields] = useState<FieldValues>([]);

  const [connected, setConnected] = useState(false);
  const [reconnectTrigger, setReconnectTrigger] = useState(0);

  const socketRef = useRef<Socket | null>(null);
  const needsRefreshRef = useRef(false);

  const { t } = useTranslate();
  const { add } = useToasts();

  const accessToken = useAtomValue(accessTokenAtom);
  const responseTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    if (connected || !accessToken) {
      return;
    }

    if (needsRefreshRef.current || isJwtExpired(accessToken)) {
      needsRefreshRef.current = false;
      void (async () => {
        try {
          await performRefreshToken();
        } catch (error) {
          console.error("Error refreshing token:", error);
          add({
            state: "error",
            title: t("openBrief.aiChat.connectionErrorToast"),
          });
        }
      })();
      return;
    }

    const socket = io(`${import.meta.env.VITE_API_BASE_URL}/ws/brief-ai`, {
      transports: ["websocket"],
      reconnection: false,
      auth: {
        token: accessToken,
      },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
    });

    socket.on("disconnect", () => {
      setConnected(false);
      setIsWaitingResponse(false);
    });

    socket.on("exception", (data: { message: string }) => {
      const msg = String(data?.message ?? "");

      if (msg.includes("Token expired")) {
        clearTimeout(responseTimeoutRef.current);
        setIsWaitingResponse(false);
        setConnected(false);
        needsRefreshRef.current = true;
        setReconnectTrigger((prev) => prev + 1);
      }
    });

    socket.on(
      "connect_error",
      (
        error: Error & {
          description?: unknown;
          context?: unknown;
          type?: string;
          data?: unknown;
        }
      ) => {
        console.error("Connection error:", {
          message: error?.message,
          name: error?.name,
          type: error?.type,
          description: error?.description,
          context: error?.context,
          data: error?.data,
          stack: error?.stack,
          transport: socket.io?.engine?.transport?.name,
          error,
        });
        setConnected(false);
        setIsWaitingResponse(false);
        add({
          state: "error",
          title: t("openBrief.aiChat.connectionErrorToast"),
        });
      }
    );

    socket.on("info", (data: { message: string }) => {
      const msg = String(data?.message ?? "");

      if (msg.includes("Connection timed out")) {
        clearTimeout(responseTimeoutRef.current);
        setIsWaitingResponse(false);
        setConnected(false);
        setReconnectTrigger((prev) => prev + 1);
      }
    });

    socket.on("conversation-ready", (response: ConversationReadyResponse) => {
      clearTimeout(responseTimeoutRef.current);
      if (response.data.message) {
        setMessages((e) => {
          const lastMessage = e?.length ? e[e.length - 1] : undefined;
          const newMessage = {
            id: lastMessage ? lastMessage.id + 1 : 0,
            message: response.data.message.content,
            sentByUser: false,
          };

          const fields = {} as Record<
            string,
            { id: number; value: string; type: string; label: string }
          >;

          response.data.fieldValues.forEach((field) => {
            fields[field.idAdma] = {
              id: Number(field.idAdma),
              label: field.label,
              value: field?.idAdmaValue || field.value,
              type: field.type,
            };
          });
          setConversationId(response.data.conversationId);
          setInitialAIValues({
            productName: response.data.productName,
            productId: response.data.productAdmaId
              ? String(response.data.productAdmaId)
              : undefined,
            deliverableId: response.data.deliverableAdmaId
              ? String(response.data.deliverableAdmaId)
              : undefined,
            fields,
            initialExpression: e[0].message,
            quantity: (response.data.quantity || 1).toString(),
            versionCount: (response.data.versionsCount || 1).toString(),
          });

          setLatestFields(response.data.fieldValues);
          return [...e, newMessage];
        });

        setIsWaitingResponse(false);
      }
    });

    socket.on("conversation-error", (error) => {
      clearTimeout(responseTimeoutRef.current);
      setIsWaitingResponse(false);
      add({
        state: "error",
        title: t("openBrief.aiChat.conversationErrorToast"),
      });
      console.error("❌ Conversation error:", error);
    });

    // Cleanup on unmount or token change
    return () => {
      clearTimeout(responseTimeoutRef.current);
      setIsWaitingResponse(false);
      setConnected(false);
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
    };
  }, [accessToken, reconnectTrigger]);

  const sendMessage = async (message: string) => {
    if (!socketRef.current?.active) {
      add({
        state: "error",
        title: t("openBrief.aiChat.connectionErrorToast"),
      });
      return;
    }

    setIsWaitingResponse(true);

    setMessages((e) => {
      const lastMessage = e?.length ? e[e.length - 1] : undefined;
      const newMessage = {
        id: lastMessage ? lastMessage.id + 1 : 0,
        message,
        sentByUser: true,
      };
      return [...e, newMessage];
    });

    const payload = {
      content: message,
      lang: getNavigatorLanguage(),
      conversationId,
      fieldValues: latestFields.filter((e) => !!e.value),
      productAdmaId: initialAIValues?.productId,
      productName: initialAIValues?.productName,
      deliverableAdmaId: initialAIValues?.deliverableId,
      versionsCount: initialAIValues?.versionCount,
      quantity: initialAIValues?.quantity,
    };

    socketRef.current.emit("send-message", payload);

    clearTimeout(responseTimeoutRef.current);
    responseTimeoutRef.current = setTimeout(() => {
      setIsWaitingResponse(false);
      add({
        state: "error",
        title: t("openBrief.aiChat.conversationErrorToast"),
      });
    }, RESPONSE_TIMEOUT_MS);
  };

  const resetChat = () => {
    setMessages([]);
    setConversationId("");
    setInitialAIValues(undefined);
    setLatestFields([]);
    setIsWaitingResponse(false);
  };

  return {
    connected,
    messages,
    sendMessage,
    resetChat,
    isWebsocketActive: socketRef.current?.active,
    initialAIValues,
    setLatestFields,
    isWaitingResponse,
  };
};
